import { base64DecodeUrlSafe, base64EncodeUrlSafe } from 'app/utils/string';
import * as iots from 'io-ts';
import { v4 } from 'uuid';

declare const GuidBrand: unique symbol;
declare const Base64GuidBrand: unique symbol;

// Branded type to declare a guid string.
export type Guid = { readonly [GuidBrand]: unique symbol } & string;

// Matches a GUID.
const GUID_REGEXP = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Creates a random guid string. Acts as a wrapper for v4.
 */
export const createGuid = (): Guid => {
  return makeGuid(v4());
};

/**
 * Casts a string to Guid, if possible.
 */
export const makeGuid = (str: string): Guid => {
  if (isGuid(str)) {
    return str;
  }

  throw new Error(`Cannot convert "${str}" to Guid.`);
};

/**
 * Type guards a string to see if it can be branded as Guid.
 */
export const isGuid = (str: string): str is Guid => {
  return GUID_REGEXP.exec(str) !== null;
};

/**
 * Typeguard as typeguards for io-ts codecs.
 *
 * @private
 */
const isGuidFromUnknown = (s: unknown): s is Guid => {
  return typeof s === 'string' && isGuid(s);
};

/**
 * Identity codec for Guid.
 */
export const IdentityGuidCodec: iots.Type<Guid, Guid, unknown> = new iots.Type(
  'GuidCodec',
  isGuidFromUnknown,
  (i, context) => {
    return isGuidFromUnknown(i) ? iots.success(i) : iots.failure(i, context, 'Does not match guid regexp.');
  },
  (a: Guid) => a,
);

/**
 * This type is primarily intended for being presented as IDs to users in the context of routing.
 *
 * Base64Guid is not formally supported by the backend, realtime server or any other engoo service.
 * For this reason, be careful about using it for encoding data that is representative of something from the backend.
 */
export type Base64Guid = { readonly [Base64GuidBrand]: unique symbol } & string;

// A valid guid encoding can be a base64 encoded URL safe string.
//
// For RFC on URL safe base64 encoded strings:
// https://tools.ietf.org/html/rfc4648#section-5
const BASE64_GUID_REGEXP = /^[0-9a-zA-Z\-_]{22}$/;

/**
 * Casts a string to a Base64Guid, if possible.
 */
export const makeBase64Guid = (str: string): Base64Guid => {
  if (isBase64Guid(str)) {
    return str;
  }

  throw new Error(`Cannot convert ${str} to Base64Guid.`);
};

/**
 * Type guards a string to see if it can be branded as an Base64Guid.
 */
export const isBase64Guid = (s: string): s is Base64Guid => {
  return BASE64_GUID_REGEXP.exec(s) !== null;
};

/**
 * Codec for strictly encoding and decoding Guids to and from Base64Guids.
 *
 * This version of a base 64 encoded guid is a URL safe version: https://tools.ietf.org/html/rfc4648#section-5
 */
export const GuidFromBase64GuidCodec: iots.Type<Guid, Base64Guid, string> = new iots.Type(
  'GuidFromBase64GuidCodec',
  isGuidFromUnknown,
  (i, context) => {
    if (typeof i !== 'string' || !isBase64Guid(i)) {
      return iots.failure(i, context, 'Does not match the format of a Base64Guid.');
    }

    try {
      // Add back padding and decode to tranditional base64 encoded characters.
      const hexString = base64DecodeUrlSafe(i)
        // Break up each character.
        .split('')
        // Turn each character into a two character hex representation.
        .map((char) => {
          const num: number = char.charCodeAt(0);

          // Error if the string contains unicode or anything that would not have come from a GUID encoding.
          if (char.length > 1 || num > 255) {
            throw new Error('Contains characters out of range.');
          }

          return num.toString(16).padStart(2, '0');
        })
        // Concatenate back into a string.
        .join('');

      // Throw an error if what we got back is not a 32 length hex string.
      if (hexString.length !== 32) {
        return iots.failure(i, context, 'Cannot be decoded into a GUID.');
      }

      // Add back hyphens and cast to Guid.
      const guid = makeGuid([
        hexString.substr(0, 8),
        hexString.substr(8, 4),
        hexString.substr(12, 4),
        hexString.substr(16, 4),
        hexString.substr(20, 12),
      ].join('-'));

      return iots.success(guid);
    } catch (e) {
      return iots.failure(i, context, e.message);
    }
  },
  (a: Guid): Base64Guid => {
    // Create buckets containing two hexadecimal characters, this can be used to turn
    // them from their 4 bit representation to their 8 bit presentation.
    const characterBuckets = a.replace(/-/g, '').match(/[0-9a-fA-F]{2}/g);

    // If the validation check above passes, this should (in theory) never happen.
    // However, match can return null and this will satisfy the type checker.
    if (characterBuckets === null) {
      throw new Error(`Cannot convert to a base64 encoded guid string: ${a}.`);
    }

    // Convert every two hexadecimals characters to a number and then to an 8 bit string. Join the results.
    const characters = characterBuckets.map((c) => parseInt(c, 16)).map((c) => String.fromCharCode(c)).join('');

    // Encode the string using btoa and wwap out URL encodable characters for non encodable characters.
    const encoded = base64EncodeUrlSafe(characters);

    // Cast to Bse64Guid.
    return makeBase64Guid(encoded);
  },
);
